// Signin flow: email + password → if MFA required, challenge → land on home.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  type MultiFactorResolver,
} from 'firebase/auth';
import { auth } from '../firebase';
import { authErrorMessage } from '../lib/auth-errors';
import MfaChallengeStep from '../components/auth/MfaChallengeStep';
import {
  AuthShell,
  Button,
  FormField,
  Input,
  Stack,
} from '../components/ui';

export default function SigninPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const navigate = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'code' in err &&
        err.code === 'auth/multi-factor-auth-required'
      ) {
        const r = getMultiFactorResolver(auth, err as Parameters<typeof getMultiFactorResolver>[1]);
        setResolver(r);
      } else {
        setError(authErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (resolver) {
    return (
      <AuthShell>
        <MfaChallengeStep
          resolver={resolver}
          onResolved={() => navigate('/', { replace: true })}
          onCancel={() => {
            setResolver(null);
            setError(null);
          }}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Stack gap={6}>
        <Stack gap={1}>
          <h1 style={{ fontSize: 'var(--fs-2xl)' }}>Welcome back</h1>
          <p className="ds-text-muted ds-text-sm">Sign in to your Rouser account.</p>
        </Stack>

        {error && <ErrorBanner message={error} />}

        <Stack gap={4} as="form" onSubmit={submit}>
          <FormField label="Email">
            {(id) => (
              <Input
                id={id}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            )}
          </FormField>

          <FormField label="Password">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            )}
          </FormField>

          <Button
            type="submit"
            variant="primary"
            block
            loading={busy}
            iconRight={!busy ? <ArrowRight size={14} strokeWidth={2} /> : undefined}
          >
            Sign in
          </Button>
        </Stack>

        <p className="ds-text-muted ds-text-sm ds-text-center">
          New here? <Link to="/signup">Create an account</Link>.
        </p>
      </Stack>
    </AuthShell>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        background: 'var(--color-danger-soft)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        color: 'var(--color-danger-text)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--fs-sm)',
      }}
    >
      {message}
    </div>
  );
}
